/**
 * CSV helpers for the OptiHub Batch Onboarding console (Team / Folder Structure
 * Batch Creator). Frontend-only concern: template generation, parsing, and the
 * browser download trigger. The actual create/list calls live in
 * useBatchOnboarding (mocked for now).
 *
 * The parser is a minimal RFC-4180 reader — it handles quoted fields, embedded
 * commas / newlines / escaped double-quotes (""), CRLF, and a leading UTF-8
 * BOM. That's the surface Excel / Google Sheets actually emit for the small
 * operator-authored sheets this tool consumes.
 */

export const FOLDER_TYPES = ['DEVICE', 'ASSET', 'PLAYLIST', 'SCHEDULE'] as const;
export type FolderType = (typeof FOLDER_TYPES)[number];

// ── Template column definitions ─────────────────────────────────────────────
export const TEAM_COLUMNS = ['name', 'description', 'maxQuantity'] as const;
export const FOLDER_COLUMNS = ['name', 'type', 'description', 'securityTeams'] as const;

export interface TeamRow {
  name: string;
  description: string;
  /** Allocated screens (maxQuantity). Raw cell value — coerced at the use site. */
  maxQuantity: string;
}
export interface FolderRow {
  name: string;
  /** One of FOLDER_TYPES (DEVICE/ASSET/PLAYLIST/SCHEDULE); validated at use site. */
  type: string;
  description: string;
  /** Single team name/id to share the folder with (the API supports one). */
  securityTeams: string;
}

// ── Parse ───────────────────────────────────────────────────────────────────
/** Parse CSV text into rows of string cells. Returns [] for empty input. */
export function parseCsv(text: string): string[][] {
  const out: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  // Strip a leading BOM if present.
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++; // CRLF → consume the \n
      row.push(field);
      field = '';
      out.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  // Flush the trailing field/row unless the input ended exactly on a newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    out.push(row);
  }
  return out;
}

/**
 * Map parsed rows to objects keyed by `columns`. The first row is treated as a
 * header when it matches the expected columns (case-insensitive); otherwise
 * rows are positional. Entirely-blank rows are dropped, and every cell is
 * trimmed.
 */
export function rowsToObjects<C extends readonly string[]>(
  rows: string[][],
  columns: C,
): Record<C[number], string>[] {
  if (rows.length === 0) return [];
  const norm = (v: string) => v.trim().toLowerCase();
  const first = rows[0].map(norm);
  const hasHeader = columns.every((c, i) => first[i] === norm(c));
  const body = hasHeader ? rows.slice(1) : rows;
  return body
    .filter((r) => r.some((c) => c.trim().length > 0))
    .map((r) => {
      const obj = {} as Record<C[number], string>;
      columns.forEach((col, i) => {
        obj[col as C[number]] = (r[i] ?? '').trim();
      });
      return obj;
    });
}

export function parseTeamCsv(text: string): TeamRow[] {
  return rowsToObjects(parseCsv(text), TEAM_COLUMNS) as TeamRow[];
}
export function parseFolderCsv(text: string): FolderRow[] {
  return rowsToObjects(parseCsv(text), FOLDER_COLUMNS) as FolderRow[];
}

/** True when `t` is one of the four valid folder types (case-insensitive). */
export function isValidFolderType(t: string): t is FolderType {
  return (FOLDER_TYPES as readonly string[]).includes(t.trim().toUpperCase());
}

// ── Serialize ─────────────────────────────────────────────────────────────--
/** Quote a cell when it contains a comma, quote, or newline. */
function escapeCell(v: string): string {
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Serialize a header + rows to CSV text (CRLF endings, Excel-friendly). */
export function toCsv(headers: readonly string[], rows: readonly string[][]): string {
  return [headers as readonly string[], ...rows]
    .map((r) => r.map(escapeCell).join(','))
    .join('\r\n');
}

// ── Templates (header + a couple of example rows) ───────────────────────────
export const TEAM_TEMPLATE_CSV = toCsv(TEAM_COLUMNS, [
  ['Team Alpha', 'Marketing team', '100'],
  ['Team Beta', 'Sales team', '50'],
]);

export const FOLDER_TEMPLATE_CSV = toCsv(FOLDER_COLUMNS, [
  ['Devices Folder', 'DEVICE', 'Folder for devices', 'team_id'],
  ['Assets Folder', 'ASSET', 'Folder for assets', 'team_id'],
  ['Playlists Folder', 'PLAYLIST', 'Folder for playlists', 'team_id'],
  ['Schedules', 'SCHEDULE', 'Folder for schedules', 'team_id'],
]);

// ── Download ──────────────────────────────────────────────────────────────--
/** Trigger a browser download of `content` as `filename` (text/csv). */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
